const ON_CHANGE_DEBOUNCE_TIMER = 300

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  optionValueSelectionChange: "option-value-selection-change",
  variantChange: "variant-change",
  cartError: "cart-error",
}

let subscribers = {}

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = []
  }

  subscribers[eventName] = [...subscribers[eventName], callback]

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback
    })
  }
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data)
    })
  }
}

function formatCurrency(amount, locale = "en-US", currency = "USD") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
  }).format(amount)
}

function formatCents(
  cents,
  {
    roundTo50 = false,
    currency = window.locale?.currency || "USD",
    symbol = window.locale?.symbol || "$",
    locale = window.locale?.locale || "en-US",
  } = {},
) {
  let value = Number(cents) / 100

  if (roundTo50) {
    // Round to nearest 0.50
    value = Math.round(value * 2) / 2
  }

  // Format the number (no currency)
  const numberFormatted = new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)

  // USD → "$12.99"
  if (currency === "USD") {
    return `${symbol}${numberFormatted}`
  }

  // All other currencies → "$12.99 CAD"
  return `${symbol}${numberFormatted} ${currency}`
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  )
}

const trapFocusHandlers = {}

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container)
  var first = elements[0]
  var last = elements[elements.length - 1]

  removeTrapFocus()

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return

    document.addEventListener("keydown", trapFocusHandlers.keydown)
  }

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown)
  }

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault()
      first.focus()
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault()
      last.focus()
    }
  }

  document.addEventListener("focusout", trapFocusHandlers.focusout)
  document.addEventListener("focusin", trapFocusHandlers.focusin)

  elementToFocus.focus()
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + "pauseVideo" + '","args":""}', "*")
  })
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*")
  })
  document.querySelectorAll("video").forEach((video) => video.pause())
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause()
  })
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin)
  document.removeEventListener("focusout", trapFocusHandlers.focusout)
  document.removeEventListener("keydown", trapFocusHandlers.keydown)

  if (elementToFocus) elementToFocus.focus()
}

class QuantityInput extends HTMLElement {
  constructor() {
    super()
    this.input = this.querySelector("input")
    this.changeEvent = new Event("change", { bubbles: true })

    this.input.addEventListener("change", this.onInputChange.bind(this))
    this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onButtonClick.bind(this)))
  }

  quantityUpdateUnsubscriber = undefined

  connectedCallback() {
    this.validateQtyRules()
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this))
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber()
    }
  }

  onInputChange(event) {
    this.validateQtyRules()
  }

  onButtonClick(event) {
    event.preventDefault()
    const previousValue = this.input.value

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown()
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent)
    this.changeBtnPrice()
  }

  changeBtnPrice() {
    const btn = document.querySelector(".product-add-to-cart")
    const value = parseInt(this.input.value)

    const $text = document.querySelector(".product-add-to-cart span:first-child")
    const $comparePriceElement = btn.querySelector(".button-text.compare_at")

    const subElm = document.querySelector("#purchaseTypeSubscription")

    const mainText = "Add To Cart"

    if (btn) {
      const price = Number(btn.getAttribute("data-price"))
      const comparePrice = Number(btn.getAttribute("data-compare-price"))

      let main = price * value

      if (subElm.checked) {
        const final = main - main * 0.15
        $text.innerText = `${mainText} - ${final.toLocaleString("en-US", { style: "currency", currency: "USD" })}`
      } else {
        $text.innerText = `${mainText} - $${main}`
      }

      if (!comparePrice && subElm.checked) {
        $comparePriceElement.innerText = Number(main).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })
      } else if (!comparePrice && !subElm) {
        $comparePriceElement.innerText = ""
      }
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value)
    if (this.input.min) {
      const min = parseInt(this.input.min)
      const buttonMinus = this.querySelector(".quantity__button[name='minus']")
      buttonMinus.classList.toggle("disabled", value <= min)
    }
    if (this.input.max) {
      const max = parseInt(this.input.max)
      const buttonPlus = this.querySelector(".quantity__button[name='plus']")
      buttonPlus.classList.toggle("disabled", value >= max)
    }
  }
}

customElements.define("quantity-input", QuantityInput)

function debounce(fn, wait) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn.apply(this, args), wait)
  }
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  }
}

function insertParam(key, value) {
  const defaultUrl = window.location.href
  const url = new URL(defaultUrl)

  let search_params = url.searchParams
  search_params.set(key, value)

  return search_params.toString()
}

function deleteParam(key) {
  let url = new URL(window.location.href)
  let params = new URLSearchParams(url.search)

  params.delete(key)

  return params.toString()
}

function getAllParam() {
  let url = new URL(window.location.href)
  let params = new URLSearchParams(url.search)

  return params.toString()
}

async function insertCartStat(type) {
  if (typeof window.recordCartStat === "function") {
    window.recordCartStat(type)
    return
  }

  try {
    const sessionId =
      sessionStorage.getItem("bbe_cart_analytics_sid") ||
      (window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`)

    try {
      sessionStorage.setItem("bbe_cart_analytics_sid", sessionId)
    } catch {
      // best-effort only
    }

    const response = await fetch("/apps/bbe-checkout-app/api/cart/view", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type,
        sessionId,
      }),
    })

    if (response.status === 429) {
      return
    }

    if (!response.ok) {
      console.log("Network error")
      return
    }

    const results = await response.json()
    console.log(results)
  } catch (error) {
    console.error(error)
  }
}

if (!customElements.get("color-swatch-radio")) {
  customElements.define(
    "color-swatch-radio",
    class extends HTMLElement {
      constructor() {
        super()

        this.btns = this.querySelectorAll('[role="radio"]')
        this.btns.forEach((btn) =>
          btn.addEventListener("click", () => {
            this.onChange(btn)
          }),
        )

        this.label = this.querySelector("label")
        this.price = this.querySelector(".color-swatch__price")

        // Call checkSelected when the custom element is loaded
        this.checkSelected()
      }

      onChange(btn) {
        this.btns.forEach((btn) => btn.classList.remove("active"))
        btn.classList.add("active")

        //Change price and label
        this.label.textContent = btn.getAttribute("data-label")
        this.price.textContent = btn.getAttribute("data-price")

        //Change form input
        const form = this.closest("form")
        const input = form.querySelector('input[name="id"]')
        input.value = btn.getAttribute("data-variant-id")

        const card = this.closest(".product-card")
        const productForm = this.closest("product-form")
        const available = btn.dataset.available === "true"
        const overlay = card?.querySelector("[data-bbe-oos-overlay]")
        if (overlay) overlay.classList.toggle("bbe-card-oos-overlay--active", !available)

        const klaviyoBis = productForm?.dataset.bbeKlaviyoBis === "true"
        const klaviyoFormId = productForm?.dataset.klaviyoBisForm || ""
        const bisLabel =
          productForm?.dataset.bbeBisLabel || window.variantStrings?.notifyMe || "Notify me when available"

        const atcButtons = card?.querySelectorAll("button.product__add__to__cart")
        atcButtons?.forEach((atc) => {
          const span = atc.querySelector(".btn-text")
          if (klaviyoBis && klaviyoFormId && !available) {
            atc.disabled = false
            atc.removeAttribute("disabled")
            atc.setAttribute("type", "button")
            atc.setAttribute("data-klaviyo-bis", "")
            atc.setAttribute("data-klaviyo-bis-form", klaviyoFormId)
            atc.classList.add("product-card__bis-cta")
            if (span) span.textContent = bisLabel
          } else if (klaviyoBis && available) {
            atc.disabled = false
            atc.removeAttribute("disabled")
            atc.setAttribute("type", "submit")
            atc.removeAttribute("data-klaviyo-bis")
            atc.removeAttribute("data-klaviyo-bis-form")
            atc.classList.remove("product-card__bis-cta")
            if (span) span.textContent = window.variantStrings?.addToCart || "Add to cart"
          } else {
            atc.disabled = !available
            atc.removeAttribute("data-klaviyo-bis")
            atc.removeAttribute("data-klaviyo-bis-form")
            atc.classList.remove("product-card__bis-cta")
            if (span) {
              span.textContent = available
                ? window.variantStrings?.addToCart || "Add to cart"
                : window.variantStrings?.outOfStock || window.variantStrings?.soldOut || "Out of stock"
            }
          }
        })
      }

      checkSelected() {
        const selected = this.querySelector(".color-swatch-btn.active")
        if (selected) this.onChange(selected)
      }
    },
  )
}

if (!customElements.get("item-card")) {
  class ItemCard extends HTMLElement {
    priceEl
    comparePriceEl
    savingsBadgeEl
    variantLabelEl
    imageEl
    addToCartBtn
    floatingAddToCartBtn
    colorButtons
    variantSelect

    connectedCallback() {
      // Cache commonly used elements inside this card
      this.priceEl = this.querySelector("[data-price]")
      this.comparePriceEl = this.querySelector("[data-compare-price]")
      this.savingsBadgeEl = this.querySelector("[data-savings-badge]")
      this.variantLabelEl = this.querySelector(".item-card__current-variant")
      /* First product image (favorite wrapper is first child; OOS overlay follows imgs) */
      this.imageEl = this.querySelector(".item-card__image > img")
      this.addToCartBtn = this.querySelector(".add-to-cart-button")
      this.floatingAddToCartBtn = this.querySelector(".item-card__cart-floating-button")

      this.colorButtons = this.querySelectorAll(".item-card__color-swatch-button")
      this.variantSelect = this.querySelector(".item-card__variant-select")
      this.variantPickerRowEl = this.querySelector(".item-card__variant-picker-row")
      this.oosOverlayEl = this.querySelector("[data-bbe-oos-overlay]")

      // Bind add to cart (marketplace OOS uses Klaviyo via document delegate; skip ajax add)
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener("click", () => {
          if (this.addToCartBtn.dataset.bbeWaitlistCta === "true") return
          if (this.addToCartBtn.disabled) return
          this.handleAddToCart()
        })
      }

      if (this.floatingAddToCartBtn) {
        this.floatingAddToCartBtn.addEventListener("click", () => {
          if (this.floatingAddToCartBtn.dataset.bbeFloatingWaitlist === "true") return
          if (this.floatingAddToCartBtn.disabled) return
          this.handleAddToCart()
        })
      }

      // Color swatches
      if (this.colorButtons.length) {
        this.initializeColorSwatches()

        this.colorButtons.forEach((btn) => {
          btn.addEventListener("click", () => {
            this.selectColorButton(btn, { focus: false })
          })

          btn.addEventListener("keydown", (event) => this.handleColorButtonKeydown(event, btn))
        })
      }

      // Fallback select option
      if (this.variantSelect) {
        this.variantSelect.addEventListener("change", (event) => {
          const option = event.target.selectedOptions[0]
          if (option) {
            this.handleVariantChange(option)
          }
        })
      }

      const defaultVariantId = this.dataset.defaultVariantId
      const defaultSelectOption =
        defaultVariantId && this.variantSelect
          ? Array.from(this.variantSelect.options).find(
              (option) => option.dataset.variantId === defaultVariantId,
            )
          : null
      const defaultColorButton =
        defaultVariantId && this.colorButtons.length
          ? Array.from(this.colorButtons).find((button) => button.dataset.variantId === defaultVariantId)
          : null

      if (defaultSelectOption && this.variantSelect) {
        this.variantSelect.value = defaultSelectOption.value
        this.handleVariantChange(defaultSelectOption)
      } else if (this.variantSelect?.selectedOptions?.[0]) {
        this.handleVariantChange(this.variantSelect.selectedOptions[0])
      } else if (this.colorButtons.length) {
        const initialBtn =
          defaultColorButton ||
          Array.from(this.colorButtons).find((b) => b.classList.contains("selected")) ||
          this.colorButtons[0]
        if (initialBtn) this.selectColorButton(initialBtn, { focus: false })
      }

      this.hydrateMarketAvailability()
    }

    handleVariantChange(sourceEl) {
      if (!sourceEl) return
      if (this.closest("cart-drawer")) return

      const variantId = sourceEl.dataset.variantId
      const available = this.getVariantAvailability(variantId, sourceEl)
      const title = sourceEl.dataset.variantTitle

      const priceFormatted = sourceEl.dataset.variantPriceFormatted || sourceEl.dataset.variantPrice

      const comparePriceFormatted =
        sourceEl.dataset.variantComparePriceFormatted || sourceEl.dataset.variantComparePrice

      const imageSrc = sourceEl.dataset.variantImage

      // Update variant title label
      if (this.variantLabelEl && title) {
        this.variantLabelEl.textContent = title
      }

      // Update price
      if (this.priceEl && priceFormatted) {
        this.priceEl.textContent = priceFormatted
      }

      // Update compare-at price
      if (this.comparePriceEl) {
        if (comparePriceFormatted && comparePriceFormatted !== "null" && comparePriceFormatted !== "") {
          this.comparePriceEl.textContent = comparePriceFormatted
          this.comparePriceEl.style.display = ""
        } else {
          this.comparePriceEl.textContent = ""
          this.comparePriceEl.style.display = "none"
        }
      }

      this.updateSavingsBadge(sourceEl)

      // Update main image (first image)
      if (this.imageEl && imageSrc) {
        this.imageEl.src = imageSrc
      }

      const klaviyoBis =
        this.dataset.bbeKlaviyoBis === "true" || this.dataset.bbeMarketplaceWaitlist === "true"
      const klaviyoFormId = this.dataset.klaviyoBisForm || ""
      const bisLabel =
        this.dataset.bbeBisLabel ||
        this.dataset.bbeJoinWaitlistLabel ||
        window.variantStrings?.notifyMe ||
        "Notify me when available"

      if (klaviyoBis && klaviyoFormId) {
        if (!available) {
          if (this.addToCartBtn) {
            this.addToCartBtn.disabled = false
            this.addToCartBtn.removeAttribute("disabled")
            this.addToCartBtn.dataset.variantId = variantId
            this.addToCartBtn.classList.add("item-card__waitlist-cta")
            this.addToCartBtn.setAttribute("data-bbe-waitlist-cta", "true")
            this.addToCartBtn.setAttribute("data-klaviyo-bis", "")
            this.addToCartBtn.setAttribute("data-klaviyo-bis-form", klaviyoFormId)
            this.updateAtcButtonContent(sourceEl, { available: false, waitlistMode: true, waitlistLabel: bisLabel })
          }
          if (this.floatingAddToCartBtn) {
            this.floatingAddToCartBtn.disabled = false
            this.floatingAddToCartBtn.removeAttribute("disabled")
            this.floatingAddToCartBtn.dataset.variantId = variantId
            this.floatingAddToCartBtn.classList.add("item-card__cart-floating-button--waitlist-mode")
            this.floatingAddToCartBtn.setAttribute("data-bbe-floating-waitlist", "true")
            this.floatingAddToCartBtn.setAttribute("data-klaviyo-bis", "")
            this.floatingAddToCartBtn.setAttribute("data-klaviyo-bis-form", klaviyoFormId)
            const titleForAria = title || "selected variant"
            this.floatingAddToCartBtn.setAttribute("aria-label", `${bisLabel} — ${titleForAria}`)
            const cartIc = this.floatingAddToCartBtn.querySelector(".item-card__floating-btn-icon--cart")
            const bellIc = this.floatingAddToCartBtn.querySelector(".item-card__floating-btn-icon--waitlist")
            if (cartIc) cartIc.classList.add("tw:hidden")
            if (bellIc) bellIc.classList.remove("tw:hidden")
          }
        } else {
          if (this.addToCartBtn) {
            this.addToCartBtn.disabled = false
            this.addToCartBtn.removeAttribute("disabled")
            this.addToCartBtn.dataset.variantId = variantId
            this.addToCartBtn.classList.remove("item-card__waitlist-cta")
            this.addToCartBtn.removeAttribute("data-bbe-waitlist-cta")
            this.addToCartBtn.removeAttribute("data-klaviyo-bis")
            this.addToCartBtn.removeAttribute("data-klaviyo-bis-form")
            this.updateAtcButtonContent(sourceEl, { available: true })
          }
          if (this.floatingAddToCartBtn) {
            this.floatingAddToCartBtn.disabled = false
            this.floatingAddToCartBtn.removeAttribute("disabled")
            this.floatingAddToCartBtn.dataset.variantId = variantId
            this.floatingAddToCartBtn.classList.remove("item-card__cart-floating-button--waitlist-mode")
            this.floatingAddToCartBtn.removeAttribute("data-bbe-floating-waitlist")
            this.floatingAddToCartBtn.removeAttribute("data-klaviyo-bis")
            this.floatingAddToCartBtn.removeAttribute("data-klaviyo-bis-form")
            const titleForAria = title || "selected variant"
            this.floatingAddToCartBtn.setAttribute("aria-label", `Quick add ${titleForAria} to cart`)
            const cartIc = this.floatingAddToCartBtn.querySelector(".item-card__floating-btn-icon--cart")
            const bellIc = this.floatingAddToCartBtn.querySelector(".item-card__floating-btn-icon--waitlist")
            if (cartIc) cartIc.classList.remove("tw:hidden")
            if (bellIc) bellIc.classList.add("tw:hidden")
          }
        }
      } else {
        if (this.addToCartBtn) {
          this.addToCartBtn.disabled = !available
          this.addToCartBtn.dataset.variantId = variantId
          this.updateAtcButtonContent(sourceEl, { available })
        }

        if (this.floatingAddToCartBtn) {
          this.floatingAddToCartBtn.disabled = !available
          this.floatingAddToCartBtn.dataset.variantId = variantId
          this.floatingAddToCartBtn.setAttribute(
            "aria-label",
            `${available ? "Quick add" : "Unavailable"} ${title || "selected variant"} ${available ? "to cart" : ""}`.trim(),
          )
        }
      }

      if (this.oosOverlayEl) {
        this.oosOverlayEl.classList.toggle("bbe-card-oos-overlay--active", !available)
      }

      if (this.variantPickerRowEl) {
        this.variantPickerRowEl.dataset.bbeSelectedPurchasable = available ? "true" : "false"
      }
    }

    getVariantAvailability(_variantId, sourceEl) {
      return sourceEl.dataset.variantAvailable === "true"
    }

    parseCents(value) {
      if (value == null || value === "" || value === "null") return 0
      const n = parseInt(String(value), 10)
      return Number.isFinite(n) ? n : 0
    }

    shouldShowCompareAt(compareCents, priceCents) {
      return compareCents > 0 && compareCents > priceCents
    }

    getVariantSavingsPercent(sourceEl) {
      if (!sourceEl?.dataset) return 0
      if (sourceEl.dataset.variantSavingsPercent != null && sourceEl.dataset.variantSavingsPercent !== "") {
        const savings = parseInt(String(sourceEl.dataset.variantSavingsPercent), 10)
        return Number.isFinite(savings) && savings > 0 ? savings : 0
      }
      const discountPct = parseFloat(sourceEl.dataset.variantDiscountPercentage)
      if (!Number.isFinite(discountPct) || discountPct <= 0 || discountPct >= 100) return 0
      return Math.round(100 - discountPct)
    }

    updateSavingsBadge(sourceEl) {
      if (!this.savingsBadgeEl) return
      const savingsPercent = this.getVariantSavingsPercent(sourceEl)
      if (savingsPercent > 0) {
        this.savingsBadgeEl.textContent = `${savingsPercent}% OFF`
        this.savingsBadgeEl.hidden = false
        this.savingsBadgeEl.removeAttribute("hidden")
      } else {
        this.savingsBadgeEl.textContent = ""
        this.savingsBadgeEl.hidden = true
        this.savingsBadgeEl.setAttribute("hidden", "")
      }
    }

    getSelectedVariantSource() {
      const selectedSwatch = Array.from(this.colorButtons).find((button) => button.classList.contains("selected"))
      if (selectedSwatch) return selectedSwatch
      return this.variantSelect?.selectedOptions?.[0] || null
    }

    updateAtcButtonContent(sourceEl, { available = true, waitlistMode = false, waitlistLabel = "" } = {}) {
      const labelSpan = this.addToCartBtn?.querySelector(".btn-text")
      if (!labelSpan) return

      if (waitlistMode) {
        labelSpan.textContent =
          waitlistLabel || this.dataset.bbeBisLabel || window.variantStrings?.notifyMe || "Notify me when available"
        return
      }

      if (!available) {
        labelSpan.textContent =
          window.variantStrings?.outOfStock || window.variantStrings?.soldOut || "Out of stock"
        return
      }

      const addLabel = window.variantStrings?.addToCart || "Add to cart"
      const priceFormatted = sourceEl?.dataset?.variantPriceFormatted || sourceEl?.dataset?.variantPrice || ""
      const priceCents = this.parseCents(sourceEl?.dataset?.variantPrice)
      const compareCents = this.parseCents(sourceEl?.dataset?.variantComparePrice)
      const compareFormatted = sourceEl?.dataset?.variantComparePriceFormatted || ""
      const showCompare = this.shouldShowCompareAt(compareCents, priceCents)

      labelSpan.innerHTML = [
        `<span class="item-card__atc-label">${addLabel}</span>`,
        `<span class="item-card__atc-sep" aria-hidden="true"> • </span>`,
        `<span class="item-card__atc-prices" data-atc-prices>`,
        showCompare && compareFormatted
          ? `<s class="item-card__atc-compare" data-atc-compare>${compareFormatted}</s>`
          : "",
        `<span class="item-card__atc-price" data-atc-price>${priceFormatted}</span>`,
        `</span>`,
      ].join("")
    }

    /** SSR `data-variant-available` on swatches/options; no app proxy. */
    hydrateMarketAvailability() {
      this.refreshSelectedVariantUi()
    }

    refreshSelectedVariantUi() {
      const selectedSwatch = Array.from(this.colorButtons).find((button) => button.classList.contains("selected"))
      if (selectedSwatch) {
        this.handleVariantChange(selectedSwatch)
        return
      }
      if (this.variantSelect?.selectedOptions?.[0]) {
        this.handleVariantChange(this.variantSelect.selectedOptions[0])
      }
    }

    initializeColorSwatches() {
      if (!this.colorButtons.length) return

      const selectedButton = Array.from(this.colorButtons).find((btn) => btn.classList.contains("selected"))
      const firstButton = this.colorButtons[0]
      this.syncColorSwatchState(selectedButton || firstButton)
    }

    syncColorSwatchState(selectedButton) {
      if (!selectedButton) return

      this.colorButtons.forEach((button) => {
        const isSelected = button === selectedButton
        button.classList.toggle("selected", isSelected)
        button.setAttribute("aria-checked", isSelected ? "true" : "false")
        button.setAttribute("tabindex", isSelected ? "0" : "-1")
      })
    }

    selectColorButton(button, { focus = true } = {}) {
      if (!button) return
      this.handleVariantChange(button)
      this.syncColorSwatchState(button)
      if (focus) {
        button.focus()
      }
    }

    handleColorButtonKeydown(event, currentButton) {
      if (!this.colorButtons.length) return

      const key = event.key
      const buttons = Array.from(this.colorButtons)
      const currentIndex = buttons.indexOf(currentButton)
      let nextIndex = null

      if (key === "ArrowRight" || key === "ArrowDown") {
        nextIndex = (currentIndex + 1) % buttons.length
      } else if (key === "ArrowLeft" || key === "ArrowUp") {
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length
      } else if (key === "Home") {
        nextIndex = 0
      } else if (key === "End") {
        nextIndex = buttons.length - 1
      } else if (key === " " || key === "Enter") {
        event.preventDefault()
        this.selectColorButton(currentButton)
        return
      } else {
        return
      }

      event.preventDefault()
      this.selectColorButton(buttons[nextIndex])
    }

    setAtcLoading(loading) {
      const btns = [this.addToCartBtn, this.floatingAddToCartBtn].filter(Boolean)
      btns.forEach((btn) => {
        btn.classList.toggle("loading", loading)
        if (loading) {
          btn.setAttribute("aria-disabled", "true")
          btn.setAttribute("disabled", "true")
          btn.setAttribute("aria-busy", "true")
        } else {
          btn.removeAttribute("aria-disabled")
          btn.removeAttribute("disabled")
          btn.removeAttribute("aria-busy")
        }
      })
    }

    showAddedChip(message) {
      const chip = this.querySelector("[data-item-card-added-chip]")
      if (!chip) return

      const text = message || "Added to cart"
      chip.textContent = text
      chip.removeAttribute("hidden")
      chip.classList.add("item-card__added-chip--visible")

      if (this._addedChipTimer) window.clearTimeout(this._addedChipTimer)
      this._addedChipTimer = window.setTimeout(() => {
        chip.classList.remove("item-card__added-chip--visible")
        window.setTimeout(() => {
          if (!chip.classList.contains("item-card__added-chip--visible")) {
            chip.setAttribute("hidden", "")
            chip.textContent = ""
          }
        }, 400)
      }, 2200)
    }

    openCartDrawer(triggerEl) {
      const attempts = [
        () => document.querySelector("cart-drawer")?.open?.(triggerEl),
        () => document.dispatchEvent(new CustomEvent("cart:open", { bubbles: true })),
        () => document.dispatchEvent(new CustomEvent("cartOpen", { bubbles: true })),
      ]
      for (const fn of attempts) {
        try {
          fn()
          return
        } catch (_) {}
      }
    }

    applyDiscountCode(code, onDone) {
      if (!code) {
        if (typeof onDone === "function") onDone()
        return
      }
      if (window.liquidAjaxCart?.update) {
        window.liquidAjaxCart.update({ discount: code }, { lastCallback: () => onDone?.() })
        return
      }
      fetch((window.routes?.cart_update_url) || "/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ discount: code }),
      })
        .catch(() => {})
        .finally(() => onDone?.())
    }

    handleAddToCart() {
      if (this.addToCartBtn?.getAttribute("data-bbe-waitlist-cta") === "true") return

      const variantId = Number(this.addToCartBtn?.dataset.variantId)
      if (!variantId) {
        console.warn("[item-card] No variant id on add-to-cart button")
        return
      }

      this.setAtcLoading(true)

      const body = { items: [{ id: variantId, quantity: 1 }] }
      const discountCode = (this.dataset.discountCode || "").trim()
      const titleEl = this.querySelector(".item-card__title")
      const productTitle = titleEl?.textContent?.trim() || "Item"

      const finishSuccess = () => {
        this.setAtcLoading(false)
        this.showAddedChip(`${productTitle} added to cart`)
        // Drawer opens via ajax-cart.js on liquid-ajax-cart:request-end (single owner).
        if (discountCode) {
          this.applyDiscountCode(discountCode)
        }
      }

      const finishError = (message) => {
        this.setAtcLoading(false)
        const labelSpan = this.addToCartBtn?.querySelector(".btn-text")
        const sourceEl = this.getSelectedVariantSource()
        const available = sourceEl ? this.getVariantAvailability(sourceEl.dataset.variantId, sourceEl) : true
        if (labelSpan) {
          labelSpan.textContent = (message || "Try again").slice(0, 24)
          window.setTimeout(() => {
            const waitlistMode = this.addToCartBtn?.dataset?.bbeWaitlistCta === "true"
            this.updateAtcButtonContent(sourceEl, {
              available: waitlistMode ? false : available,
              waitlistMode,
              waitlistLabel: this.dataset.bbeBisLabel || window.variantStrings?.notifyMe,
            })
          }, 2200)
        }
      }

      if (window.liquidAjaxCart?.add) {
        window.liquidAjaxCart.add(body, {
          lastCallback: (requestState) => {
            const ok = requestState?.responseData?.ok !== false
            if (ok) finishSuccess()
            else finishError(requestState?.responseData?.body?.description)
          },
        })
        return
      }

      fetch((window.routes?.cart_add_url) || "/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then((result) => {
          if (!result.ok || result.data?.status) throw new Error(result.data?.description || "Could not add to cart")
          finishSuccess()
        })
        .catch((err) => finishError(err?.message))
    }
  }

  customElements.define("item-card", ItemCard)
}

// Market Switcher
if (!customElements.get("localization-form")) {
  class LocalizationForm extends HTMLElement {
    constructor() {
      super()
    }

    connectedCallback() {
      // One listener per instance; handles all current and future buttons
      this.addEventListener("click", (event) => {
        const button = event.target.closest("button.market-select__menu-button")
        if (!button || !this.contains(button)) return

        this.onItemClick(event, button)
      })
    }

    onItemClick(event, button) {
      event.preventDefault()

      button.classList.add("loading")

      // 🔍 Use the button to find its own form
      const form = button.closest("form")
      if (!form) {
        console.warn("No form found for this button", button)
        return
      }

      // 🔍 Find the input *inside that form*
      const input = form.querySelector('input[name="language_code"], input[name="country_code"]')

      if (!input) {
        console.warn("No localization input found in this form", form)
        return
      }

      input.value = button.dataset.value
      form.submit()
    }
  }

  customElements.define("localization-form", LocalizationForm)
}
